import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CompassOutlined,
  FileDoneOutlined,
  FlagOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { Button, Card, Divider, Empty, Input, Modal, Select, Space, Tag, Timeline, Typography, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { contactsApi } from '../../services/contactsApi';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const STAGE_META = {
  submitted: { label: 'Submitted', color: 'blue', icon: <FileDoneOutlined /> },
  reviewed: { label: 'Reviewed', color: 'geekblue', icon: <CompassOutlined /> },
  interview_1_scheduled: { label: 'Interview 1 Scheduled', color: 'gold', icon: <ClockCircleOutlined /> },
  interview_1_completed: { label: 'Interview 1 Completed', color: 'cyan', icon: <CheckCircleOutlined /> },
  interview_2_scheduled: { label: 'Interview 2 Scheduled', color: 'gold', icon: <ClockCircleOutlined /> },
  interview_2_completed: { label: 'Interview 2 Completed', color: 'cyan', icon: <CheckCircleOutlined /> },
  offer_extended: { label: 'Offer Extended', color: 'purple', icon: <FlagOutlined /> },
  offer_accepted: { label: 'Offer Accepted', color: 'green', icon: <CheckCircleOutlined /> },
  hired: { label: 'Hired', color: 'green', icon: <UsergroupAddOutlined /> },
  rejected: { label: 'Rejected', color: 'red', icon: <StopOutlined /> },
  withdrawn: { label: 'Withdrawn', color: 'default', icon: <StopOutlined /> }
};

const QUICK_STAGES = [
  'reviewed',
  'interview_1_scheduled',
  'interview_1_completed',
  'interview_2_scheduled',
  'interview_2_completed',
  'offer_extended',
  'offer_accepted',
  'hired',
  'rejected',
  'withdrawn'
];

const getCandidateId = (candidate) =>
  candidate?.id ||
  candidate?.candidate_id ||
  candidate?.documentId ||
  candidate?._id ||
  candidate?.contact?.id ||
  candidate?.contactId ||
  '';

const getJobId = (job) =>
  job?.id ||
  job?.job_id ||
  job?._id ||
  job?.documentId ||
  job?.jobId ||
  '';

const getCandidateSnapshot = (candidate) => ({
  id: getCandidateId(candidate),
  name: candidate?.contactName || candidate?.name || candidate?.fullName || 'Unknown Candidate',
  title: candidate?.job_title || candidate?.title || '',
  email: candidate?.email || candidate?.contact?.email || '',
  phone: candidate?.phone || candidate?.contact?.phone || '',
  location: candidate?.location || candidate?.contact?.location || ''
});

const getJobSnapshot = (job) => ({
  id: getJobId(job),
  title: job?.title || job?.job_title || 'Unknown Job',
  company: job?.company || job?.employer_name || '',
  location: job?.location || ''
});

const normalizeJobs = (payload) => {
  const rawJobs = Array.isArray(payload?.job_descriptions)
    ? payload.job_descriptions
    : (Array.isArray(payload?.jobs) ? payload.jobs : []);

  return rawJobs
    .map((job) => ({
      ...job,
      id: getJobId(job),
      title: job?.title || job?.job_title || 'Untitled Role',
      company: job?.company || job?.employer_name || '',
      location: job?.location || ''
    }))
    .filter((job) => Boolean(job.id));
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? String(value) : dt.toLocaleString();
};

const PlacementJourneyPanel = ({ candidate }) => {
  const candidateId = useMemo(() => getCandidateId(candidate), [candidate]);
  const lastEvaluatedJob = candidate?.lastEvaluatedJob || candidate?.contact?.lastEvaluatedJob || null;

  const [loading, setLoading] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [selectedJourneyId, setSelectedJourneyId] = useState('');
  const [journeyDetail, setJourneyDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [newNote, setNewNote] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [createJourneyOpen, setCreateJourneyOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    round: 'first',
    scheduled_at: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Singapore',
    interviewer: '',
    meeting_location: '',
    notes: ''
  });

  const selectedJourney = journeyDetail?.journey || journeys.find((item) => item.journey_id === selectedJourneyId || item.id === selectedJourneyId) || null;

  const loadJourneys = async () => {
    if (!candidateId) {
      setJourneys([]);
      setJourneyDetail(null);
      setSelectedJourneyId('');
      return;
    }

    try {
      setLoading(true);
      const resp = await contactsApi.listPlacementJourneys(candidateId);
      const list = Array.isArray(resp?.journeys) ? resp.journeys : [];
      setJourneys(list);

      if (!list.length) {
        setSelectedJourneyId('');
        setJourneyDetail(null);
        return;
      }

      const preferredJobId = getJobId(lastEvaluatedJob);
      const preferred =
        list.find((item) => item.job_id === preferredJobId) ||
        list.find((item) => item.stage === 'submitted') ||
        list[0];

      if (preferred) {
        setSelectedJourneyId(preferred.journey_id || preferred.id);
      }
    } catch (error) {
      message.error(error?.message || 'Failed to load placement journeys');
    } finally {
      setLoading(false);
    }
  };

  const loadJourneyDetail = async (journey) => {
    const jid = journey?.journey_id || journey?.id;
    if (!candidateId || !jid) {
      setJourneyDetail(null);
      return;
    }

    try {
      const resp = await contactsApi.getPlacementJourney(candidateId, journey.job_id);
      if (resp?.success && resp?.journey) {
        setJourneyDetail(resp);
      } else {
        setJourneyDetail({ journey });
      }
    } catch (error) {
      setJourneyDetail({ journey });
    }
  };

  useEffect(() => {
    loadJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, lastEvaluatedJob?.id]);

  useEffect(() => {
    const journey = journeys.find((item) => (item.journey_id || item.id) === selectedJourneyId);
    if (journey) {
      loadJourneyDetail(journey);
    } else {
      setJourneyDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJourneyId, journeys]);

  const createJourneyFromLatestJob = async () => {
    const job = lastEvaluatedJob;
    const jobId = getJobId(job);
    if (!candidateId || !jobId) {
      message.warning('No job context available to create a placement journey');
      return;
    }

    try {
      setActionLoading('create');
      const resp = await contactsApi.upsertPlacementJourney({
        candidate_id: candidateId,
        job_id: jobId,
        stage: 'submitted',
        status: 'active',
        source: 'candidate_profile',
        actor: 'human_user',
        channel: 'ui',
        submission: {
          submitted_at: new Date().toISOString(),
          submitted_by: 'alice_agent',
          submission_method: 'manual',
          cv_sent: false,
          jd_sent: false,
          evaluation_report_sent: false
        },
        candidate_snapshot: getCandidateSnapshot(candidate),
        job_snapshot: getJobSnapshot(job),
        metadata: {
          created_from: 'candidate_profile_panel'
        }
      });

      if (resp?.success) {
        message.success('Placement journey created');
        await loadJourneys();
      } else {
        message.error(resp?.error || 'Failed to create placement journey');
      }
    } catch (error) {
      message.error(error?.message || 'Failed to create placement journey');
    } finally {
      setActionLoading('');
    }
  };

  const openCreateJourney = async () => {
    setCreateJourneyOpen(true);
    if (jobs.length) return;

    try {
      setJobsLoading(true);
      const resp = await contactsApi.listJobs(100);
      if (!resp?.success) {
        message.error(resp?.error || 'Failed to load jobs');
        return;
      }
      setJobs(normalizeJobs(resp));
    } catch (error) {
      message.error(error?.message || 'Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  const createJourneyForSelectedJob = async () => {
    const job = jobs.find((item) => item.id === selectedJobId);
    if (!candidateId || !job) {
      message.warning('Select a job first');
      return;
    }

    try {
      setActionLoading('create-manual');
      const resp = await contactsApi.upsertPlacementJourney({
        candidate_id: candidateId,
        job_id: job.id,
        stage: 'submitted',
        status: 'active',
        source: 'candidate_profile',
        actor: 'human_user',
        channel: 'ui',
        event_type: 'journey_created_manually',
        summary: `Manual placement journey created for ${job.title}`,
        submission: {
          submitted_at: new Date().toISOString(),
          submitted_by: 'human_user',
          submission_method: 'manual',
          cv_sent: false,
          jd_sent: false,
          evaluation_report_sent: false
        },
        candidate_snapshot: getCandidateSnapshot(candidate),
        job_snapshot: getJobSnapshot(job),
        metadata: {
          created_from: 'candidate_profile_panel',
          manual: true
        }
      });

      if (!resp?.success) {
        message.error(resp?.error || 'Failed to create placement journey');
        return;
      }

      message.success('Placement journey created');
      setCreateJourneyOpen(false);
      setSelectedJobId('');
      await loadJourneys();
      const createdId = resp?.journey?.journey?.journey_id || resp?.journey?.journey_id;
      if (createdId) setSelectedJourneyId(createdId);
    } catch (error) {
      message.error(error?.message || 'Failed to create placement journey');
    } finally {
      setActionLoading('');
    }
  };

  const applyStage = async (stage) => {
    const jid = selectedJourney?.journey_id || selectedJourney?.id;
    if (!jid) {
      message.warning('Select a placement journey first');
      return;
    }

    try {
      setActionLoading(stage);
      const resp = await contactsApi.updatePlacementJourneyStage(jid, {
        stage,
        event_type: 'stage_updated_manually',
        actor: 'human_user',
        channel: 'ui',
        status: ['rejected', 'withdrawn'].includes(stage) ? 'closed' : 'completed',
        journey_status: ['hired', 'offer_accepted'].includes(stage) ? 'placed' : ['rejected', 'withdrawn'].includes(stage) ? 'closed' : 'active',
        summary: STAGE_META[stage]?.label || stage,
        notes: newNote || '',
        next_action: stage === 'offer_extended' ? 'Follow up on offer response' : '',
        feedback: {}
      });

      if (resp?.success) {
        message.success('Journey updated');
        setNewNote('');
        await loadJourneys();
      } else {
        message.error(resp?.error || 'Failed to update journey');
      }
    } catch (error) {
      message.error(error?.message || 'Failed to update journey');
    } finally {
      setActionLoading('');
    }
  };

  const addNote = async () => {
    const jid = selectedJourney?.journey_id || selectedJourney?.id;
    if (!jid) {
      message.warning('Select a placement journey first');
      return;
    }
    if (!newNote.trim()) {
      message.warning('Enter a note first');
      return;
    }

    try {
      setActionLoading('note');
      const resp = await contactsApi.addPlacementJourneyEvent(jid, {
        event_type: 'note_added',
        summary: 'Note added',
        actor: 'alice_agent',
        channel: 'ui',
        status: 'completed',
        notes: newNote,
        details: { note: newNote }
      });

      if (resp?.success) {
        message.success('Note added');
        setNewNote('');
        await loadJourneys();
      } else {
        message.error(resp?.error || 'Failed to add note');
      }
    } catch (error) {
      message.error(error?.message || 'Failed to add note');
    } finally {
      setActionLoading('');
    }
  };

  const logClientInterview = async () => {
    const jid = selectedJourney?.journey_id || selectedJourney?.id;
    if (!jid) {
      message.warning('Select a placement journey first');
      return;
    }
    if (!interviewForm.scheduled_at) {
      message.warning('Enter the interview date and time');
      return;
    }

    const stage =
      interviewForm.round === 'first' ? 'interview_1_scheduled' : 'interview_2_scheduled';
    const roundLabel =
      interviewForm.round === 'first'
        ? 'First client interview'
        : interviewForm.round === 'second'
          ? 'Second client interview'
          : 'Final client interview';

    try {
      setActionLoading('interview');
      const resp = await contactsApi.updatePlacementJourneyStage(jid, {
        stage,
        event_type: 'client_interview_scheduled',
        actor: 'human_user',
        channel: 'ui',
        status: 'scheduled',
        journey_status: 'active',
        summary: `${roundLabel} scheduled`,
        notes: interviewForm.notes,
        next_action: `Attend ${roundLabel.toLowerCase()}`,
        details: {
          interview_type: interviewForm.round,
          scheduled_at: interviewForm.scheduled_at,
          timezone: interviewForm.timezone,
          interviewer: interviewForm.interviewer,
          meeting_location: interviewForm.meeting_location,
          job_id: selectedJourney.job_id,
          candidate_id: candidateId
        }
      });

      if (!resp?.success) {
        message.error(resp?.error || 'Failed to log client interview');
        return;
      }

      message.success('Client interview logged');
      setInterviewOpen(false);
      setInterviewForm({
        round: 'first',
        scheduled_at: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Singapore',
        interviewer: '',
        meeting_location: '',
        notes: ''
      });
      await loadJourneys();
    } catch (error) {
      message.error(error?.message || 'Failed to log client interview');
    } finally {
      setActionLoading('');
    }
  };

  const events = Array.isArray(selectedJourney?.events) ? selectedJourney.events : [];

  return (
    <Card
      size="small"
      title="Placement Journey"
      extra={(
        <Space wrap>
          <Button icon={<PlusOutlined />} onClick={openCreateJourney}>
            Create Journey
          </Button>
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() => setInterviewOpen(true)}
            disabled={!selectedJourney}
          >
            Log Interview
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadJourneys} loading={loading}>
            Refresh
          </Button>
        </Space>
      )}
    >
      {!candidateId ? (
        <Empty description="No candidate selected" />
      ) : (
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Journey</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select a placement journey"
                  value={selectedJourneyId || undefined}
                  onChange={setSelectedJourneyId}
                  loading={loading}
                  options={journeys.map((journey) => {
                    const meta = STAGE_META[journey.stage] || {};
                    return {
                      value: journey.journey_id || journey.id,
                      label: `${journey.job_snapshot?.title || journey.job_snapshot?.company || journey.job_id || 'Journey'} - ${meta.label || journey.stage || 'Unknown'}`
                    };
                  })}
                />
              </div>
            </div>

            {selectedJourney ? (
              <>
                <Space wrap>
                  <Tag color={STAGE_META[selectedJourney.stage]?.color || 'default'}>
                    {STAGE_META[selectedJourney.stage]?.label || selectedJourney.stage || 'Unknown'}
                  </Tag>
                  <Tag>{selectedJourney.status || 'active'}</Tag>
                  <Tag>{selectedJourney.source || 'candidate_search'}</Tag>
                </Space>

                <div>
                  <Text strong>Job</Text>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {selectedJourney.job_snapshot?.title || selectedJourney.job_id}
                    {selectedJourney.job_snapshot?.company ? ` · ${selectedJourney.job_snapshot.company}` : ''}
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Summary</Text>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {selectedJourney.timeline_summary?.summary || 'No summary yet'}
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Quick Stage Updates</Text>
                  <div style={{ marginTop: 8 }}>
                    <Space wrap>
                      {QUICK_STAGES.map((stage) => (
                        <Button
                          key={stage}
                          size="small"
                          onClick={() => applyStage(stage)}
                          loading={actionLoading === stage}
                        >
                          {STAGE_META[stage]?.label || stage}
                        </Button>
                      ))}
                    </Space>
                  </div>
                </div>

                <div>
                  <Text strong>Note</Text>
                  <div style={{ marginTop: 8 }}>
                    <TextArea
                      rows={3}
                      placeholder="Add an internal note or interview update"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                    />
                    <div style={{ marginTop: 8 }}>
                      <Button type="primary" icon={<PlusOutlined />} onClick={addNote} loading={actionLoading === 'note'}>
                        Add Note
                      </Button>
                    </div>
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div>
                  <Text strong>Timeline</Text>
                  <div style={{ marginTop: 12 }}>
                    {events.length ? (
                      <Timeline
                        items={events.map((event) => ({
                          color: STAGE_META[event.stage_after || event.event_type]?.color || 'blue',
                          children: (
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {event.summary || event.event_type || 'Event'}
                              </div>
                              <div style={{ fontSize: 12, color: '#777' }}>
                                {formatDateTime(event.created_at)} · {event.actor || 'alice_agent'}
                              </div>
                              {event.notes ? (
                                <div style={{ marginTop: 4, fontSize: 13 }}>
                                  {event.notes}
                                </div>
                              ) : null}
                            </div>
                          )
                        }))}
                      />
                    ) : (
                      <Empty description="No placement events yet" />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Empty description="No placement journey found" />
                <div style={{ marginTop: 12 }}>
                  <Space wrap>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateJourney}>
                      Create Journey
                    </Button>
                    {lastEvaluatedJob && getJobId(lastEvaluatedJob) ? (
                    <Button type="primary" onClick={createJourneyFromLatestJob} loading={actionLoading === 'create'}>
                      Create Journey from Latest Job
                    </Button>
                    ) : null}
                  </Space>
                </div>
              </div>
            )}
          </Space>
        </>
      )}

      <Modal
        title="Create Placement Journey"
        open={createJourneyOpen}
        onCancel={() => {
          setCreateJourneyOpen(false);
          setSelectedJobId('');
        }}
        onOk={createJourneyForSelectedJob}
        okText="Create Journey"
        confirmLoading={actionLoading === 'create-manual'}
        destroyOnClose
      >
        <Text type="secondary">
          Select the job this candidate is being considered for.
        </Text>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: '100%', marginTop: 16 }}
          placeholder="Search and select a job"
          value={selectedJobId || undefined}
          onChange={setSelectedJobId}
          loading={jobsLoading}
          options={jobs.map((job) => ({
            value: job.id,
            label: `${job.title}${job.company ? ` · ${job.company}` : ''}`
          }))}
        />
      </Modal>

      <Modal
        title="Log Client Interview"
        open={interviewOpen}
        onCancel={() => setInterviewOpen(false)}
        onOk={logClientInterview}
        okText="Save Interview"
        confirmLoading={actionLoading === 'interview'}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Job</Text>
            <div>
              {selectedJourney?.job_snapshot?.title || selectedJourney?.job_id || 'No journey selected'}
              {selectedJourney?.job_snapshot?.company
                ? ` · ${selectedJourney.job_snapshot.company}`
                : ''}
            </div>
          </div>
          <div>
            <Text strong>Interview round</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={interviewForm.round}
              onChange={(round) => setInterviewForm((current) => ({ ...current, round }))}
              options={[
                { value: 'first', label: 'First client interview' },
                { value: 'second', label: 'Second client interview' },
                { value: 'final', label: 'Final client interview' }
              ]}
            />
          </div>
          <div>
            <Text strong>Date and time</Text>
            <Input
              type="datetime-local"
              style={{ marginTop: 8 }}
              value={interviewForm.scheduled_at}
              onChange={(event) =>
                setInterviewForm((current) => ({
                  ...current,
                  scheduled_at: event.target.value
                }))
              }
            />
          </div>
          <div>
            <Text strong>Timezone</Text>
            <Input
              style={{ marginTop: 8 }}
              value={interviewForm.timezone}
              onChange={(event) =>
                setInterviewForm((current) => ({ ...current, timezone: event.target.value }))
              }
            />
          </div>
          <div>
            <Text strong>Client interviewer</Text>
            <Input
              style={{ marginTop: 8 }}
              placeholder="Name or email"
              value={interviewForm.interviewer}
              onChange={(event) =>
                setInterviewForm((current) => ({ ...current, interviewer: event.target.value }))
              }
            />
          </div>
          <div>
            <Text strong>Location or meeting link</Text>
            <Input
              style={{ marginTop: 8 }}
              placeholder="Google Meet, Zoom, office address..."
              value={interviewForm.meeting_location}
              onChange={(event) =>
                setInterviewForm((current) => ({
                  ...current,
                  meeting_location: event.target.value
                }))
              }
            />
          </div>
          <div>
            <Text strong>Notes</Text>
            <TextArea
              rows={3}
              style={{ marginTop: 8 }}
              value={interviewForm.notes}
              onChange={(event) =>
                setInterviewForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </div>
        </Space>
      </Modal>
    </Card>
  );
};

export default PlacementJourneyPanel;
